import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { setError } from '../../../actions';
import {
  CurrentResidenceQuery,
  ResidenceItemsQuery
} from '../../../graphql/graphql';
import { CurrentItemLayout } from '../../Ad/CurrentItemLayout';
import { useFullPhoneNumbers } from '../../../hooks/useFullPhoneNumbers';
import ResidencesNotFoundSection from '../../Ad/ResidencesNotFoundSection';
import { ResidenceContentWrapper } from '../../Ad/CurrentItemLayout/styled';
import { CurrentItemBreadcrumbs } from '../../Ad/Sections/CurrentItemBreadcrumbs';
import { ResidencePageSchema } from './ResidencePageSchema';
import { AnalyticLogTargetTypeEnum } from '../../../graphql/types';
import PhotosGallery from '../../Ad/PhotosGallery';
import DevelopmentList from '../../Ad/DevelopmentList';
import InfoSection from '../../Ad/Sections/InfoSection';
import AdditionalInfoSection from '../../Ad/Sections/AdditionalInfoSection';
import LocationSection from '../../Ad/Sections/LocationSection';
import ParamsSection from '../../Ad/Sections/ParamsSection';
import DescriptionSection from '../../Ad/Sections/DescriptionSection';
import VideoSection from '../../Ad/VideoSection';
import InfrastructureSection from '../../Ad/Sections/InfrastructureSection';
import ItemsSection from '../../Ad/Sections/ItemsSection';
import OwnerSection from '../../Ad/Sections/OwnerSection';
import ApartmentsFilter from '../../Ad/ApartmentsFilter';

const BottomPhoneButton = dynamic(
  () => import('../../BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false
  }
);

const mapDispatchToProps = dispatch => {
  return {
    setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
  };
};

type ResidenceEntity = NonNullable<CurrentResidenceQuery['residence']>;
interface ResidenceProps {
  setErrorUI: (arg: boolean) => void;
  residence?: CurrentResidenceQuery['residence'] | null;
  residenceItems?: ResidenceItemsQuery['items'] | null;
  residenceSlug: string;
  hasError?: boolean;
}

const ResidencePageComponent: React.FC<ResidenceProps> = ({
  setErrorUI,
  residence = null,
  residenceItems,
  residenceSlug,
  hasError = false
}) => {
  const { t } = useTranslation();
  const { isWebView } = useBridgeState();
  const residenceData: Partial<ResidenceEntity> = residence ?? {};
  const { onClickHandler, phonesShown, setIsVisible, isVisible } =
    useFullPhoneNumbers({
      id: residenceSlug,
      residenceId: residenceData.id ?? null,
      path: 'yasayis-kompleksleri',
      targetType: AnalyticLogTargetTypeEnum.Residence,
      agencyId: null
    });

  useEffect(() => {
    if (hasError) setErrorUI(true);
  }, [hasError]);

  const breadcrumbs = [
    {
      name: t('sections.residences.title'),
      path: '/yasayis-kompleksleri'
    },
    { name: residenceData.companyName }
  ];

  return (
    <>
      {residence && <ResidencePageSchema residence={residence} />}
      <ResidencesNotFoundSection isEmpty={!residence}>
        <CurrentItemLayout isResidencePage>
          {!isWebView && <CurrentItemBreadcrumbs breadcrumbs={breadcrumbs} />}
          <PhotosGallery
            photos={residenceData.photos}
            residence={residenceData}
            {...{ onClickHandler, phonesShown }}
          />
          <ResidenceContentWrapper>
            <DevelopmentList
              developer={residenceData.companyName}
              imageUrl={residenceData.logoUrl}
              deadline={residenceData.deadline}
            />
            <InfoSection
              priceMax={residenceData.price?.total?.maximum}
              priceMin={residenceData.price?.total?.minimum}
              currency={residenceData.price?.currency}
              deadline={residenceData.deadline}
              apartments={residenceData.apartments}
              hasBillOfSale={residenceData.hasBillOfSale}
            />
            {residenceData.specialOffers?.length > 0 && (
              <AdditionalInfoSection
                specialOffers={residenceData.specialOffers}
              />
            )}
            <LocationSection
              city={residenceData.city?.name}
              address={residenceData.address}
              locations={residenceData.locations}
              lat={residenceData.lat}
              lng={residenceData.lng}
            />
            <ParamsSection {...residenceData} />
            <DescriptionSection
              description={residenceData.description}
              isOneDirection={true}
            />

            {(residenceData.youtubeVideoId ||
              residenceData.constructionProgresses?.length > 0) && (
              <VideoSection
                videoId={residenceData.youtubeVideoId}
                width={200}
                height={112}
                construction={residenceData.constructionProgresses}
                name={residenceData.name}
                deadline={residenceData.deadline}
              />
            )}
            {residenceData.infrastructures?.length > 0 && (
              <InfrastructureSection
                infrastructures={residenceData.infrastructures}
              />
            )}
            <OwnerSection
              ownerName={residenceData.name}
              imageUrl={residenceData.logoUrl}
              phonesMasked={residenceData.phonesMasked}
              contactEmail={residenceData.contactEmail}
              website={residenceData.website}
              brochureUrl={residenceData.brochureUrl}
              clickHandler={onClickHandler}
              {...{ phonesShown, residenceSlug }}
            />
            {residenceData.apartments?.length > 0 && (
              <ApartmentsFilter
                description={t('pages.residence.residence_name', {
                  residence_name: residenceData?.name
                })}
                apartmentRooms={residenceData.apartmentRooms}
                filterCardList={residenceData.apartments}
              />
            )}
            {residenceData.itemsCount > 0 && (
              <ItemsSection
                residenceId={residenceData.id}
                name={residenceData.name}
                itemsRooms={residenceData.itemRooms}
                itemsCount={residenceData.itemsCount}
                residenceItems={residenceItems}
              />
            )}
            <BottomPhoneButton
              stat="residences-phones-btn"
              phones={phonesShown}
              clickHandler={onClickHandler}
              {...{ setIsVisible, isVisible }}
            />
          </ResidenceContentWrapper>
        </CurrentItemLayout>
      </ResidencesNotFoundSection>
    </>
  );
};

export const ResidencePage = connect(
  null,
  mapDispatchToProps
)(ResidencePageComponent);
